"use client"

import styled from "@emotion/styled"
import { extractProperty, replaceRichTextLinks } from "utils"
import { useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

// --- Styled Components ---

/* Figma 687:2519 / 613:9795 — desktop padding is 120 top, 120 side, 88 bottom. */
const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: 64px 20px;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 120px 120px 88px;
  }
`

/* Stacked and centered on all breakpoints (ASH-1628). */
const Inner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 32px;
  }
`

/* Headline and subtitle are one cluster in Figma, tighter than the gap that
   separates them from the body copy: 8 on mobile, 16 on desktop. */
const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
  }
`

const Headline = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 40px;
  color: var(--comp-heading);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    line-height: 72px;
  }
`

/* The `text` field is the subtitle, not body copy — it was rendering at body
   size (16 mobile / 18 desktop) against a spec of 28 / 40 (ASH-1629). */
const Subtitle = styled.div`
  font-size: 28px;
  font-weight: 700;
  line-height: 40px;
  color: var(--comp-heading);
  width: 100%;

  p {
    margin: 0;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
    line-height: 40px;
  }
`

/* Explanation has no cards/buttons, so it uses only the primary color package
   (ASH-1627) — no --inner-* vars. */
const Text = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: var(--comp-text);
  width: 100%;

  p {
    margin: 0 0 12px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  a {
    color: var(--comp-heading);
    text-decoration: underline;
  }

  /* The copy is centered, so a marker positioned outside the content box is
     pushed to the far edge and detaches from its link (ASH-1628). Rendering it
     inside keeps the dot next to the text. */
  ul,
  ol {
    margin: 0;
    padding: 0;
    list-style-position: inside;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    font-weight: 700;
    line-height: 27px;
  }
`

// --- Main Component ---

function Explanation({ data }: Props) {
  const analytics = useLandingAnalytics()
  const headline = extractProperty(data, "headline") as string | ""
  const subtitle = extractProperty(data, "text") as string | ""
  const body = extractProperty(data, "additionalText") as string | ""

  if (!headline && !subtitle && !body) return null

  /* The rich text is injected as HTML, so its links have no React props -
     delegate from the wrapper and read the anchor that was hit (ASH-1854). */
  const handleRichTextClick = (e: React.MouseEvent<HTMLElement>) => {
    const anchor = (e.target as HTMLElement).closest("a")
    if (anchor) analytics.textLinkClick(anchor.textContent?.trim() || "")
  }

  return (
    <Section>
      <Inner>
        {(headline || subtitle) && (
          <TitleGroup>
            {headline && <Headline>{headline}</Headline>}
            {/* rich-text links resolved so internal/PDF links work (ASH-1626) */}
            {subtitle && (
              <Subtitle
                onClick={handleRichTextClick}
                dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(subtitle, true) }}
              />
            )}
          </TitleGroup>
        )}
        {body && (
          <Text
            onClick={handleRichTextClick}
            dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(body, true) }}
          />
        )}
      </Inner>
    </Section>
  )
}

export default Explanation
